import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { TECHNICAL_DOCUMENT_TYPES, TECHNICAL_DOCUMENT_TYPE_LABELS, currentRevisionOf, type TechnicalDocumentWithRevisions } from "../../domain/documentation/technical-document.js";
import { BOOK_CONTEXT_TYPE_LABELS } from "../../domain/inspection-book/book-context-resolver.js";

interface Option { id: string; label: string }

/**
 * DOCUMENTAÇÃO — duas telas (Bloco 03): BIBLIOTECA (consulta/filtros/
 * visualizar/baixar) e ANEXAR DOCUMENTO (upload vinculado por IDs reais).
 * A rota decide o modo: /documentation/attach = ANEXAR, qualquer outra
 * (/documentation, /documentation/library) = BIBLIOTECA.
 */
class AppDocumentationPage extends HTMLElement {
  private mode: "library" | "attach" = "library";

  private isLoadingFilters = true;
  private isLoadingResults = false;
  private clients: Option[] = [];
  private projects: Array<Option & { clientId: string }> = [];
  private unitAreas: Array<Option & { projectId: string }> = [];
  private items: Array<Option & { contextTypeLabel: string }> = [];

  private selectedClientId = "";
  private selectedProjectId = "";
  private selectedUnitAreaId = "";
  private selectedEquipmentId = "";
  private selectedDocumentType = "";
  private selectedFrom = "";
  private selectedTo = "";

  private results: TechnicalDocumentWithRevisions[] = [];
  private searchError = "";

  // Anexar
  private uploadEquipmentId = "";
  private uploadDocumentType = "";
  private uploadTitle = "";
  private uploadDescription = "";
  private uploadRevision = "";
  private uploadDate = "";
  private uploadFile: File | null = null;
  private uploadBusy = false;
  private uploadMessage: { kind: "ok" | "error"; text: string } | null = null;

  async connectedCallback() {
    this.mode = window.location.pathname === "/documentation/attach" ? "attach" : "library";
    this.render();
    await this.loadFilterOptions();
    if (this.mode === "library") await this.runSearch();
  }

  private get platform(): any {
    return (window as any).app?.platform;
  }

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: this.selectedClientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private can(permission: string): boolean {
    return contextStore.hasPermission(permission as any);
  }

  private async loadFilterOptions() {
    this.isLoadingFilters = true;
    this.render();
    const p = this.platform;
    try {
      const clients: any[] = (await p?.adminService?.listAuthorizedClients(this.ctx()).catch(() => [])) || [];
      this.clients = clients.map(c => ({ id: String(c.id), label: c.fantasyName || c.name || String(c.id) }));
      if (!this.selectedClientId && this.clients.length === 1) this.selectedClientId = this.clients[0]!.id;
      await this.loadProjects();
    } catch (e) {
      console.warn("[DOCUMENTAÇÃO] Não foi possível carregar Clientes", e);
    } finally {
      this.isLoadingFilters = false;
      this.render();
    }
  }

  private async loadProjects() {
    const p = this.platform;
    const projects: any[] = (await p?.projectService?.listProjects(this.selectedClientId || undefined).catch(() => [])) || [];
    this.projects = projects.map(pr => ({ id: String(pr.id), label: [pr.code, pr.name].filter(Boolean).join(" - ") || String(pr.id), clientId: String(pr.clientId || "") }));
    await this.loadUnitAreas();
  }

  private async loadUnitAreas() {
    const p = this.platform;
    const filter: any = {};
    if (this.selectedClientId) filter.clientId = this.selectedClientId;
    if (this.selectedProjectId) filter.projectId = this.selectedProjectId;
    const units: any[] = (await p?.projectService?.listUnitAreas(Object.keys(filter).length ? filter : undefined).catch(() => [])) || [];
    this.unitAreas = units.map(u => ({ id: String(u.id), label: [u.code, u.name].filter(Boolean).join(" - ") || String(u.id), projectId: String(u.projectId || "") }));
    await this.loadItems();
  }

  private async loadItems() {
    const p = this.platform;
    const consolidator = p?.inspectionBookConsolidatorService;
    if (!consolidator) { this.items = []; return; }
    // Reaproveita o mesmo resolvedor de Equipamento/TAG do Book de Inspeção — nenhum segundo seletor de contexto.
    const rows = await consolidator.listItems(this.ctx(), { projectId: this.selectedProjectId || null, unitAreaId: this.selectedUnitAreaId || null }).catch(() => []);
    this.items = rows.map((r: any) => ({ id: r.id, label: `${r.tag || r.name} — ${r.name}`.trim(), contextTypeLabel: r.contextTypeLabel }));
  }

  private async runSearch() {
    this.isLoadingResults = true;
    this.searchError = "";
    this.render();
    const p = this.platform;
    try {
      const filter: any = {};
      if (this.selectedProjectId) filter.projectId = this.selectedProjectId;
      if (this.selectedUnitAreaId) filter.unitAreaId = this.selectedUnitAreaId;
      if (this.selectedEquipmentId) filter.equipmentId = this.selectedEquipmentId;
      if (this.selectedDocumentType) filter.documentType = this.selectedDocumentType;
      if (this.selectedFrom) filter.from = this.selectedFrom;
      if (this.selectedTo) filter.to = this.selectedTo;
      filter.status = "ACTIVE";
      this.results = await p.technicalDocumentService.search(this.ctx(), filter);
    } catch (e: any) {
      console.error("[DOCUMENTAÇÃO] Falha na busca", e);
      this.searchError = e?.safeMessage || e?.message || "Não foi possível consultar a Biblioteca.";
      this.results = [];
    } finally {
      this.isLoadingResults = false;
      this.render();
    }
  }

  private async openRevision(documentId: string, revisionId: string) {
    const p = this.platform;
    try {
      const url = await p.technicalDocumentService.resolveDownload(this.ctx(), revisionId, documentId);
      if (url) window.open(url, "_blank", "noopener");
    } catch (e: any) {
      alert(e?.safeMessage || e?.message || "Não foi possível abrir o documento.");
    }
  }

  private async submitUpload() {
    if (this.uploadBusy) return;
    if (!this.uploadEquipmentId || !this.uploadDocumentType || !this.uploadTitle.trim() || !this.uploadFile) {
      this.uploadMessage = { kind: "error", text: "Preencha Equipamento/TAG, Tipo de Documento, Título e selecione o arquivo." };
      this.render();
      return;
    }
    this.uploadBusy = true;
    this.uploadMessage = null;
    this.render();
    try {
      const fileContent = await this.readFileAsDataUrl(this.uploadFile);
      const p = this.platform;
      await p.technicalDocumentService.upload(this.ctx(), {
        equipmentId: this.uploadEquipmentId,
        documentType: this.uploadDocumentType,
        title: this.uploadTitle.trim(),
        description: this.uploadDescription.trim() || null,
        fileContent,
        filename: this.uploadFile.name,
        mimeType: this.uploadFile.type || "application/octet-stream",
        sizeBytes: this.uploadFile.size,
        revision: this.uploadRevision.trim() || undefined,
        documentDate: this.uploadDate || null
      });
      this.uploadMessage = { kind: "ok", text: `Documento anexado com sucesso (${this.uploadFile.name}).` };
      this.uploadTitle = ""; this.uploadDescription = ""; this.uploadRevision = ""; this.uploadDate = ""; this.uploadFile = null;
      const fileInput = this.querySelector("#f-upload-file") as HTMLInputElement | null;
      if (fileInput) fileInput.value = "";
    } catch (e: any) {
      this.uploadMessage = { kind: "error", text: e?.safeMessage || e?.message || "Não foi possível anexar o documento." };
    } finally {
      this.uploadBusy = false;
      this.render();
    }
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  // ------------------------------------------------------------------ render

  private render() {
    this.innerHTML = `<style>${this.styles()}</style>
      <div class="docp">
        <header class="docp-head">
          <nav class="crumb"><span class="crumb-root">INSPECT IA</span><span class="crumb-sep">›</span><span class="crumb-current">DOCUMENTAÇÃO</span></nav>
          <h1 class="docp-title">${this.mode === "attach" ? "Anexar Documento" : "Biblioteca Técnica"}</h1>
          <p class="docp-sub">${this.mode === "attach" ? "Anexe documentos técnicos vinculados ao Equipamento real — nunca por nome de arquivo." : "Consulta consolidada dos documentos técnicos anexados, com revisão vigente e histórico."}</p>
        </header>

        <div class="tabs">
          <a class="tab ${this.mode === "library" ? "active" : ""}" href="/documentation/library" data-link>Biblioteca</a>
          ${this.can(PermissionCode.DocumentWrite) ? `<a class="tab ${this.mode === "attach" ? "active" : ""}" href="/documentation/attach" data-link>Anexar Documento</a>` : ""}
        </div>

        ${this.mode === "attach" ? this.renderAttach() : this.renderLibrary()}
      </div>`;
    this.bind();
  }

  private renderContextFilters(includeStatusless = true): string {
    const projects = this.selectedClientId ? this.projects.filter(p => p.clientId === this.selectedClientId) : this.projects;
    const units = this.selectedProjectId ? this.unitAreas.filter(u => u.projectId === this.selectedProjectId) : this.unitAreas;
    return `
      <label class="filter">
        <span class="filter-label">Cliente</span>
        <select class="ctl" id="f-client" ${this.isLoadingFilters ? "disabled" : ""}>
          <option value="">Selecione…</option>
          ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${this.selectedClientId === c.id ? "selected" : ""}>${this.esc(c.label)}</option>`).join("")}
        </select>
      </label>
      <label class="filter">
        <span class="filter-label">Projeto</span>
        <select class="ctl" id="f-project" ${this.isLoadingFilters ? "disabled" : ""}>
          <option value="">${includeStatusless ? "Todos" : "Selecione…"}</option>
          ${projects.map(p => `<option value="${this.esc(p.id)}" ${this.selectedProjectId === p.id ? "selected" : ""}>${this.esc(p.label)}</option>`).join("")}
        </select>
      </label>
      <label class="filter">
        <span class="filter-label">Unidade / Área</span>
        <select class="ctl" id="f-unit" ${this.isLoadingFilters ? "disabled" : ""}>
          <option value="">${includeStatusless ? "Todas" : "Selecione…"}</option>
          ${units.map(u => `<option value="${this.esc(u.id)}" ${this.selectedUnitAreaId === u.id ? "selected" : ""}>${this.esc(u.label)}</option>`).join("")}
        </select>
      </label>`;
  }

  private renderLibrary(): string {
    return `
      <section class="filters" aria-label="Filtros da Biblioteca">
        ${this.renderContextFilters()}
        <label class="filter">
          <span class="filter-label">Equipamento / TAG</span>
          <select class="ctl" id="f-item" ${this.isLoadingFilters ? "disabled" : ""}>
            <option value="">Todos</option>
            ${this.items.map(i => `<option value="${this.esc(i.id)}" ${this.selectedEquipmentId === i.id ? "selected" : ""}>${this.esc(i.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Tipo de Documento</span>
          <select class="ctl" id="f-type">
            <option value="">Todos</option>
            ${TECHNICAL_DOCUMENT_TYPES.map(t => `<option value="${t.code}" ${this.selectedDocumentType === t.code ? "selected" : ""}>${this.esc(t.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Período</span>
          <span class="period">
            <input class="ctl" type="date" id="f-from" value="${this.esc(this.selectedFrom)}">
            <span class="period-sep">→</span>
            <input class="ctl" type="date" id="f-to" value="${this.esc(this.selectedTo)}">
          </span>
        </label>
        <div class="filter-actions">
          <button class="btn btn-primary" id="btn-search" type="button">Filtrar</button>
        </div>
      </section>

      ${this.isLoadingResults ? this.renderSkeleton() : this.searchError ? this.emptyCard("Não foi possível carregar", this.searchError) : this.renderResults()}`;
  }

  private renderResults(): string {
    if (this.results.length === 0) {
      return this.emptyCard("Nenhum documento encontrado", "Ajuste os filtros ou anexe o primeiro documento técnico deste contexto.");
    }
    return `
      <section class="card">
        <table class="doc-table">
          <thead><tr><th>Tipo</th><th>TAG</th><th>Documento</th><th>Revisão</th><th>Data</th><th>Projeto</th><th></th></tr></thead>
          <tbody>
            ${this.results.map(entry => {
              const rev = currentRevisionOf(entry);
              const project = this.projects.find(p => p.id === entry.document.projectId);
              return `
                <tr>
                  <td>${this.esc(TECHNICAL_DOCUMENT_TYPE_LABELS[entry.document.documentType] || entry.document.documentType)}</td>
                  <td><strong>${this.esc(entry.document.equipmentTag)}</strong><br><small>${this.esc(BOOK_CONTEXT_TYPE_LABELS[entry.document.contextType])}</small></td>
                  <td>${this.esc(entry.document.title)}${entry.document.description ? `<br><small>${this.esc(entry.document.description)}</small>` : ""}</td>
                  <td>${this.esc(rev?.revision || "—")}</td>
                  <td>${this.esc((rev?.documentDate || rev?.uploadedAt || "").slice(0, 10))}</td>
                  <td>${this.esc(project?.label || "—")}</td>
                  <td class="doc-actions">
                    ${rev ? `<button class="link-action" data-view="${entry.document.id}:${rev.id}" type="button">VISUALIZAR</button><button class="link-action" data-download="${entry.document.id}:${rev.id}" type="button">BAIXAR</button>` : ""}
                  </td>
                </tr>`;
            }).join("")}
          </tbody>
        </table>
      </section>`;
  }

  private renderAttach(): string {
    if (!this.can(PermissionCode.DocumentWrite)) {
      // UX Correção: mensagem redundante removida; botão de anexo já é ocultado via permissão
      return this.emptyCard("Sem documentos", "Nenhum documento técnico anexado até o momento.");
    }
    return `
      <section class="card attach-card">
        ${this.uploadMessage ? `<div class="notice ${this.uploadMessage.kind === "ok" ? "notice-ok" : "notice-error"}">${this.esc(this.uploadMessage.text)}</div>` : ""}
        <div class="attach-grid">
          ${this.renderContextFilters(false)}
          <label class="filter">
            <span class="filter-label">Tipo de Contexto</span>
            <input class="ctl" type="text" value="${this.esc(this.uploadEquipmentId ? (this.items.find(i => i.id === this.uploadEquipmentId)?.contextTypeLabel || "") : "")}" readonly placeholder="Selecione o Equipamento/TAG">
          </label>
          <label class="filter filter-wide">
            <span class="filter-label">Equipamento / TAG *</span>
            <select class="ctl" id="f-upload-item" ${this.isLoadingFilters ? "disabled" : ""}>
              <option value="">Selecione…</option>
              ${this.items.map(i => `<option value="${this.esc(i.id)}" ${this.uploadEquipmentId === i.id ? "selected" : ""}>${this.esc(i.label)} · ${this.esc(i.contextTypeLabel)}</option>`).join("")}
            </select>
          </label>
          <label class="filter">
            <span class="filter-label">Tipo de Documento *</span>
            <select class="ctl" id="f-upload-type">
              <option value="">Selecione…</option>
              ${TECHNICAL_DOCUMENT_TYPES.map(t => `<option value="${t.code}" ${this.uploadDocumentType === t.code ? "selected" : ""}>${this.esc(t.label)}</option>`).join("")}
            </select>
          </label>
          <label class="filter">
            <span class="filter-label">Arquivo *</span>
            <input class="ctl" type="file" id="f-upload-file">
          </label>
          <label class="filter">
            <span class="filter-label">Revisão</span>
            <input class="ctl" type="text" id="f-upload-revision" value="${this.esc(this.uploadRevision)}" placeholder="Ex.: 00, A, Rev.1">
          </label>
          <label class="filter">
            <span class="filter-label">Data</span>
            <input class="ctl" type="date" id="f-upload-date" value="${this.esc(this.uploadDate)}">
          </label>
          <label class="filter filter-wide">
            <span class="filter-label">Título *</span>
            <input class="ctl" type="text" id="f-upload-title" value="${this.esc(this.uploadTitle)}" placeholder="Ex.: Isométrico TK-101 Rev. 00">
          </label>
          <label class="filter filter-wide">
            <span class="filter-label">Descrição / Observação</span>
            <input class="ctl" type="text" id="f-upload-desc" value="${this.esc(this.uploadDescription)}">
          </label>
        </div>
        <div class="filter-actions" style="margin-top:14px;">
          <button class="btn btn-primary" id="btn-upload" type="button" ${this.uploadBusy ? "disabled" : ""}>${this.uploadBusy ? "Anexando…" : "Anexar Documento"}</button>
        </div>
      </section>`;
  }

  private renderSkeleton(): string {
    return `<div class="card sk"><div class="sk-line w40"></div><div class="sk-line w60 tall"></div><div class="sk-line w30"></div></div>`;
  }
  private emptyCard(title: string, desc: string): string {
    return `<div class="card empty"><div class="empty-title">${this.esc(title)}</div><div class="empty-desc">${this.esc(desc)}</div></div>`;
  }

  // ------------------------------------------------------------------ events

  private bind() {
    const client = this.querySelector("#f-client") as HTMLSelectElement | null;
    client?.addEventListener("change", async () => { this.selectedClientId = client.value; this.selectedProjectId = ""; this.selectedUnitAreaId = ""; this.selectedEquipmentId = ""; this.uploadEquipmentId = ""; await this.loadProjects(); this.render(); });
    const project = this.querySelector("#f-project") as HTMLSelectElement | null;
    project?.addEventListener("change", async () => { this.selectedProjectId = project.value; this.selectedUnitAreaId = ""; this.selectedEquipmentId = ""; this.uploadEquipmentId = ""; await this.loadUnitAreas(); this.render(); });
    const unit = this.querySelector("#f-unit") as HTMLSelectElement | null;
    unit?.addEventListener("change", async () => { this.selectedUnitAreaId = unit.value; this.selectedEquipmentId = ""; this.uploadEquipmentId = ""; await this.loadItems(); this.render(); });

    if (this.mode === "library") {
      (this.querySelector("#f-item") as HTMLSelectElement | null)?.addEventListener("change", e => { this.selectedEquipmentId = (e.target as HTMLSelectElement).value; });
      (this.querySelector("#f-type") as HTMLSelectElement | null)?.addEventListener("change", e => { this.selectedDocumentType = (e.target as HTMLSelectElement).value; });
      (this.querySelector("#f-from") as HTMLInputElement | null)?.addEventListener("change", e => { this.selectedFrom = (e.target as HTMLInputElement).value; });
      (this.querySelector("#f-to") as HTMLInputElement | null)?.addEventListener("change", e => { this.selectedTo = (e.target as HTMLInputElement).value; });
      this.querySelector("#btn-search")?.addEventListener("click", () => { void this.runSearch(); });
      this.querySelectorAll<HTMLElement>("[data-view]").forEach(el => el.addEventListener("click", () => { const [docId, revId] = el.dataset["view"]!.split(":"); void this.openRevision(docId!, revId!); }));
      this.querySelectorAll<HTMLElement>("[data-download]").forEach(el => el.addEventListener("click", () => { const [docId, revId] = el.dataset["download"]!.split(":"); void this.openRevision(docId!, revId!); }));
    } else {
      (this.querySelector("#f-upload-item") as HTMLSelectElement | null)?.addEventListener("change", e => { this.uploadEquipmentId = (e.target as HTMLSelectElement).value; this.render(); });
      (this.querySelector("#f-upload-type") as HTMLSelectElement | null)?.addEventListener("change", e => { this.uploadDocumentType = (e.target as HTMLSelectElement).value; });
      (this.querySelector("#f-upload-file") as HTMLInputElement | null)?.addEventListener("change", e => { this.uploadFile = (e.target as HTMLInputElement).files?.[0] || null; });
      (this.querySelector("#f-upload-revision") as HTMLInputElement | null)?.addEventListener("input", e => { this.uploadRevision = (e.target as HTMLInputElement).value; });
      (this.querySelector("#f-upload-date") as HTMLInputElement | null)?.addEventListener("change", e => { this.uploadDate = (e.target as HTMLInputElement).value; });
      (this.querySelector("#f-upload-title") as HTMLInputElement | null)?.addEventListener("input", e => { this.uploadTitle = (e.target as HTMLInputElement).value; });
      (this.querySelector("#f-upload-desc") as HTMLInputElement | null)?.addEventListener("input", e => { this.uploadDescription = (e.target as HTMLInputElement).value; });
      this.querySelector("#btn-upload")?.addEventListener("click", () => { void this.submitUpload(); });
    }
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private styles(): string {
    return `
      .docp { display: flex; flex-direction: column; gap: 16px; }
      .crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; }
      .crumb-root { color: var(--ia-fg-soft, #9BA4AE); }
      .crumb-sep { color: var(--ia-fg-muted, rgba(255,255,255,0.45)); }
      .crumb-current { color: var(--ia-fg, #C8D2DC); font-weight: 600; }
      .docp-title { margin: 6px 0 2px; font-size: clamp(1.4rem, 2.2vw, 1.9rem); font-weight: 600; color: var(--ia-fg, #E7EAED); }
      .docp-sub { margin: 0; color: var(--ia-fg-muted, #8A939D); font-size: 0.875rem; }

      .tabs { display: flex; gap: 4px; border-bottom: 1px solid rgba(255,255,255,0.08); }
      .tab { padding: 10px 16px; font-size: 0.8rem; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); text-decoration: none; border-bottom: 2px solid transparent; }
      .tab.active { color: var(--ia-fg, #E7EAED); border-bottom-color: #C9A227; }

      .filters { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; align-items: end; padding: 14px; border: 1px solid var(--ia-line, rgba(255,255,255,0.08)); border-radius: 10px; background: var(--ia-bg-panel, #12161A); }
      .filter { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .filter-wide { grid-column: span 2; }
      .filter-label { font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .ctl { width: 100%; padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.10)); color: var(--ia-fg, #DCE2E8); font-size: 0.82rem; font-family: inherit; }
      .period { display: grid; grid-template-columns: 1fr auto 1fr; gap: 6px; align-items: center; }
      .period-sep { color: var(--ia-fg-muted, #8A939D); }
      .filter-actions { display: flex; }
      .btn { padding: 9px 16px; border-radius: 8px; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; cursor: pointer; border: 1px solid transparent; }
      .btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .btn-primary { background: var(--ia-bg-raised, #2B3A47); color: var(--ia-fg, #E7EAED); border-color: var(--ia-line, #3A4C5C); }
      .btn-primary:hover:not(:disabled) { background: var(--ia-bg-raised, #334554); }

      .card { background: var(--ia-bg-panel, #12161A); border: 1px solid var(--ia-line, rgba(255,255,255,0.08)); border-radius: 10px; padding: 16px; }
      .attach-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
      .notice { padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; font-size: 0.82rem; }
      .notice-ok { background: rgba(110,158,126,0.14); color: var(--ia-fg, #B7D8C2); border: 1px solid rgba(110,158,126,0.3); }
      .notice-error { background: rgba(167,111,111,0.14); color: var(--ia-danger-fg, #E3B9B9); border: 1px solid rgba(167,111,111,0.3); }

      .doc-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
      .doc-table th { text-align: left; font-size: 0.62rem; letter-spacing: 0.07em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); padding: 6px 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.08)); }
      .doc-table td { padding: 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.05)); color: var(--ia-fg-soft, #C3CAD1); vertical-align: middle; }
      .doc-actions { display: flex; gap: 10px; white-space: nowrap; }
      .link-action { background: none; border: none; padding: 0; font-size: 0.68rem; font-weight: 600; letter-spacing: 0.05em; color: var(--ia-info-fg, #9BB4CC); cursor: pointer; }
      .link-action:hover { text-decoration: underline; }

      .empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 6px; padding: 28px 12px; color: var(--ia-fg-muted, #8A939D); min-height: 96px; }
      .empty-title { font-size: 0.85rem; color: var(--ia-fg-soft, #C3CAD1); font-weight: 600; }
      .empty-desc { font-size: 0.75rem; max-width: 420px; }

      .sk { min-height: 104px; display: flex; flex-direction: column; gap: 10px; justify-content: center; }
      .sk.tall { min-height: 200px; }
      .sk-line { height: 10px; border-radius: 4px; background: linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.10), rgba(255,255,255,0.05)); background-size: 200% 100%; animation: sk 1.2s ease-in-out infinite; }
      .sk-line.tall { height: 24px; }
      .w30 { width: 30%; } .w40 { width: 40%; } .w60 { width: 60%; }
      @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      @media (max-width: 1100px) {
        .filters, .attach-grid { grid-template-columns: repeat(2, 1fr); }
        .filter-wide { grid-column: span 2; }
      }
      @media (max-width: 640px) {
        .filters, .attach-grid { grid-template-columns: 1fr; }
        .filter-wide { grid-column: span 1; }
      }`;
  }
}

if (!customElements.get("app-documentation-page")) {
  customElements.define("app-documentation-page", AppDocumentationPage);
}
